import { useEffect, useRef, useState } from "react"
import type { MouseEvent } from "react"
import { createPortal } from "react-dom"
import classnames from "classnames"
import parse from "html-react-parser"

import ServiceList from "./service_list"

import arrowIcon from "images/icons/dropdown_arrow.svg?raw"
import copyIcon from "images/icons/copy.svg?raw"

import styles from "./ingredient_listbox.module.scss"

interface Props {
  apiList: ApiList
  name: string
  serviceList?: Service[]
}

interface ApiList {
  options: Array<string> | Record<string, Array<string>>
  placeholder: string
  helpText?: {
    [key: string]: string
  }
}

interface Service {
  image: string
  module_name: string
  name: string
  normalized_module_name: string
}

const COPIED_TOAST_MS = 1500

const IngredientListbox = (props: Props) => {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(copiedTimerRef.current), [])

  if (!props.apiList?.options) return null

  // Selecting the label is the visible trace of the copy, as the frame draws it.
  const copyContent = (e: MouseEvent<HTMLButtonElement>) => {
    const button = e.currentTarget

    const label = button.querySelector("span")
    const selection = window.getSelection()
    if (label && selection) {
      const range = document.createRange()
      range.selectNodeContents(label)
      selection.removeAllRanges()
      selection.addRange(range)
    }

    navigator.clipboard
      .writeText(button.title)
      .then(() => {
        setCopied(true)
        clearTimeout(copiedTimerRef.current)
        copiedTimerRef.current = setTimeout(() => setCopied(false), COPIED_TOAST_MS)
      })
      .catch(() => {})
  }

  // As the frames draw it: the service once, then each path after it; a click
  // copies the full name, which also names the button.
  const ingredient = (name: string, service: string) => (
    <li key={name}>
      <button type="button" className={styles.ingredient} title={name} aria-label={name} onClick={copyContent}>
        {parse(copyIcon)}
        <span>{name.slice(service.length)}</span>
      </button>
    </li>
  )

  // Arrays (DataStore) are statements, not one service's members, so they keep their full text.
  const groups = (options: Array<string> | Record<string, Array<string>>): Array<[string, Array<string>]> =>
    Array.isArray(options)
      ? [["", options]]
      : Object.entries(options).map(([service, members]) => [service, members.map(member => `${service}.${member}`)])

  const renderLimits = () => {
    if (!props.apiList.helpText) return null

    return (
      <ul className={styles["help-text"]}>
        {Object.values(props.apiList.helpText).map(value => (
          <li key={value}>{value}</li>
        ))}
      </ul>
    )
  }

  return (
    <section className={styles.menu}>
      <header className={styles.title}>
        <h3 className={classnames("text-h4 phone-text-h5", styles.name)}>{props.name}</h3>
        {props.serviceList?.length ? (
          <span className={styles.services}>
            <ServiceList context={props.name} services={props.serviceList} />
          </span>
        ) : null}
      </header>

      <div className={styles.box}>
        <button type="button" className={styles.toggle} aria-expanded={open} onClick={() => setOpen(!open)}>
          <span>{props.apiList.placeholder}</span>
          <span className={styles.arrow}>{parse(arrowIcon)}</span>
        </button>
        {open && (
          <>
            {groups(props.apiList.options).map(([service, members]) => (
              <div key={service} className={styles.group}>
                {service && <h4 className={styles.service}>{service}</h4>}
                <ul className={styles.ingredients}>{members.map(name => ingredient(name, service))}</ul>
              </div>
            ))}
            {renderLimits()}
          </>
        )}
      </div>

      {/* The live region stays mounted: text dropped into a fresh one is not announced. */}
      {createPortal(
        <div role="status" className={copied ? styles.toast : styles["toast-idle"]}>
          {copied && "Copied to clipboard"}
        </div>,
        document.body,
      )}
    </section>
  )
}

export default IngredientListbox
